import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSpinner, FaCheckCircle, FaExclamationCircle, FaGift, FaMoon, FaSun } from "react-icons/fa";
import Logo from "../Assets/BooksCompl.png";

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState({ content: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [isInviteSignup, setIsInviteSignup] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Dark mode effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  // Get referral code or bookkeeping invite from URL on mount
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }

    const invite = searchParams.get('invite');
    if (!invite) return;

    setInviteToken(invite);
    setIsInviteSignup(true);
    setInviteLoading(true);

    const loadInvite = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/bookkeeping-invite/${invite}`
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Invalid invitation link');
        }
        setFormData((prev) => ({
          ...prev,
          email: data.email || prev.email,
          name: data.name || prev.name,
        }));
      } catch (err) {
        setMessage({ content: err.message, type: 'danger' });
      } finally {
        setInviteLoading(false);
      }
    };

    loadInvite();
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, password, confirmPassword } = formData;

    if (!agreeToTerms) {
      setMessage({ content: "You must agree to the terms and conditions", type: "danger" });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ content: "Passwords do not match", type: "danger" });
      return;
    }

    if (password.length < 6) {
      setMessage({ content: "Password must be at least 6 characters", type: "danger" });
      return;
    }

    setLoading(true);

    try {
      const endpoint = isInviteSignup
        ? `${process.env.REACT_APP_API_BASE_URL}/api/auth/complete-bookkeeping-invite`
        : `${process.env.REACT_APP_API_BASE_URL}/api/auth/signup`;

      const body = isInviteSignup
        ? { token: inviteToken, name, password }
        : { name, email, password, referralCode };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setMessage({
        content: data.message || (isInviteSignup
          ? "Account created! Please check your email to verify your account, then log in."
          : "Registration successful! Please check your email to verify your account."),
        type: "success"
      });

      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

    } catch (error) {
      setMessage({
        content: error.message || "An error occurred during registration",
        type: "danger"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="fixed top-6 right-6 z-50 p-3 rounded-xl bg-white dark:bg-dark-800 shadow-lg border border-gray-200 dark:border-dark-700 hover:scale-105 transition-all duration-200"
        aria-label="Toggle theme"
      >
        {isDarkMode ? (
          <FaSun className="w-5 h-5 text-yellow-500" />
        ) : (
          <FaMoon className="w-5 h-5 text-primary-600" />
        )}
      </button>

      {/* Left Side - Branding & Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/30 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

          {/* 3D Floating Elements */}
          <div className="absolute top-20 left-20 w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl opacity-20 animate-float rotate-12" style={{ animationDelay: '0s' }} />
          <div className="absolute bottom-32 right-20 w-16 h-16 bg-gradient-to-br from-primary-300 to-primary-500 rounded-xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-gradient-to-br from-white to-gray-200 rounded-lg opacity-10 animate-float" style={{ animationDelay: '1s' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img src={Logo} alt="BooksComply" className="h-16 w-auto mb-8 drop-shadow-lg" />
          <h2 className="text-4xl font-bold mb-4 text-center">Join BooksComply</h2>
          <p className="text-lg text-white/80 text-center max-w-md">
            Start your journey with professional accounting and tax services.
          </p>

          {/* Feature List */}
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <FaCheckCircle className="w-5 h-5" />
              </div>
              <span className="text-white/90">Easy & Secure Registration</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <FaCheckCircle className="w-5 h-5" />
              </div>
              <span className="text-white/90">Access All Services Instantly</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <FaCheckCircle className="w-5 h-5" />
              </div>
              <span className="text-white/90">24/7 Expert Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md animate-scale-in py-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={Logo} alt="BooksComply" className="h-12 w-auto mx-auto" />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-2">
              {isInviteSignup ? "Complete Your Registration" : "Create Account"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isInviteSignup
                ? "Create a password to access your assigned Bookkeeping section"
                : "Join BooksComply today"}
            </p>
          </div>

          {/* Referral Badge */}
          {referralCode && (
            <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 flex items-center gap-3 animate-slide-up">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <FaGift className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">Referral Applied!</p>
                <p className="text-xs text-green-600 dark:text-green-500">You're signing up with a referral code</p>
              </div>
            </div>
          )}

          {/* Message Alert */}
          {message.content && (
            <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 animate-slide-up ${message.type === "success"
                ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400"
                : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
              }`}>
              {message.type === "success"
                ? <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                : <FaExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              }
              <span className="text-sm font-medium">{message.content}</span>
            </div>
          )}

          {inviteLoading && (
            <div className="mb-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 text-sm">
              Loading your invitation details...
            </div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaUser className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type="text"
                name="name"
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Email Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaEnvelope className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type="email"
                name="email"
                readOnly={isInviteSignup}
                className={`w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 ${
                  isInviteSignup
                    ? 'bg-gray-100 dark:bg-dark-700 cursor-not-allowed'
                    : 'bg-white dark:bg-dark-800'
                }`}
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Password Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-primary-500 transition-colors"
              >
                {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
              </button>
            </div>

            {/* Confirm Password Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaLock className="w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-primary-500 transition-colors"
              >
                {showConfirmPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
              </button>
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <label htmlFor="agreeToTerms" className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                By signing up, you agree to the{" "}
                <a
                  href={process.env.REACT_APP_PRIVACY_POLICY_URL}
                  className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Notice
                </a>
                {" "}and{" "}
                <a
                  href={process.env.REACT_APP_SUBSCRIPTION_AGREEMENT_URL}
                  className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Subscription Agreement
                </a>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || inviteLoading || !agreeToTerms || (isInviteSignup && !inviteToken)}
              className="w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:from-primary-700 hover:to-primary-600 active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-5 h-5 animate-spin" />
                  {isInviteSignup ? "Setting Up Account..." : "Creating Account..."}
                </>
              ) : (isInviteSignup ? "Create Password & Continue" : "Create Account")}
            </button>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-center text-gray-600 dark:text-gray-400">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;